import {
  ACTIVITY_FACTORS,
  type ActivityLevel,
  addDays,
  ageYearsOn,
  bodyDerived,
  checkTargetAgainstTrend,
  DIET_BREAK_WEEKS,
  defaultSecondaryTargets,
  deviceTimeZone,
  GOAL_PHASES,
  type GoalPhase,
  localDateString,
  observedTdee,
  PHASE_RATES,
  type PhaseAdvice,
  type ProfilePatch,
  phaseAdvice,
  phaseFromTarget,
  phaseRun,
  planForPhase,
  restingEnergy,
  type SessionUser,
  weightTrendPerWeek,
} from '@gramas/shared'
import {
  Button,
  Card,
  Field,
  ListRow,
  NumberField,
  SectionLabel,
  Segmented,
  Sheet,
  Switch,
} from '@gramas/ui'
import { useQuery } from '@tanstack/react-query'
import { useLiveQuery } from 'dexie-react-hooks'
import { useEffect, useRef, useState } from 'react'
import { api, logout } from '../api/client.js'
import { db, getMeta } from '../db/dexie.js'
import {
  DEFAULT_GOAL,
  exportDiaryCsv,
  exportMeasurementsCsv,
  goalForDate,
  goalHistory,
  intakeStats,
  latestMeasurements,
  latestWeightKg,
  saveGoal,
} from '../db/repo.js'
import { DossierSheet } from '../glitime/DossierSheet.js'
import { sassLine } from '../sass/engine.js'
import { playCue } from '../sound/engine.js'
import { setSoundEnabled, useSoundSetting } from '../sound/settings.js'
import { countPending, syncNow } from '../sync/engine.js'
import { saveTheme, storedTheme, type Theme } from '../theme.js'
import { startTour } from '../tour/store.js'
import { APP_VERSION, copyrightLine } from '../version.js'
import { Admin } from './Admin.js'
import { RemindersCard } from './RemindersCard.js'
import { useProfile } from './useProfile.js'

/**
 * The day itself, not the training in it.
 *
 * Treniruotės are logged (or read off the watch) and added to that day's limit,
 * so counting them here as well paid for them twice and halved the deficit
 * anybody on this screen thought they had chosen. What this asks about is the
 * part of the day nothing else measures: whether it is spent in a chair.
 */
const ACTIVITY_OPTIONS: Array<{ value: ActivityLevel; label: string; hint: string }> = [
  { value: 'sedentary', label: 'Sėdimas', hint: 'Biuras, automobilis, nedaug vaikščiojimo' },
  { value: 'light', label: 'Nedidelis', hint: 'Šiek tiek vaikštoma per dieną' },
  { value: 'moderate', label: 'Vidutinis', hint: 'Beveik visą dieną ant kojų' },
  { value: 'heavy', label: 'Fizinis darbas', hint: 'Statybos, sandėlis, ūkis' },
]

const PHASE_ORDER: readonly GoalPhase[] = ['cut', 'maintain', 'bulk']

const PHASE_LABEL: Readonly<Record<GoalPhase, string>> = {
  cut: 'Metimas',
  maintain: 'Išlaikymas',
  bulk: 'Auginimas',
}

/**
 * What a phase this long is worth saying, in the terms someone dieting would
 * put it. None of these move anything by themselves.
 */
const ADVICE_HINT: Readonly<Record<Exclude<PhaseAdvice, 'none'>, string>> = {
  diet_break: `Po ${DIET_BREAK_WEEKS} sav. deficito apykaita jau būna nukritusi. Savaitė ar dvi ties išlaikymu paprastai grąžina daugiau, nei kainuoja — ir metimas po jų vyksta greičiau.`,
  to_maintenance:
    'Tokio ilgio deficite riba dažniausiai yra nebe skaičiai, o ištvermė. Verta kurį laiką palaikyti pasiektą svorį ir grįžti prie metimo vėliau.',
  long_bulk:
    'Raumenys auga mėnesiais, riebalai — savaitėmis. Ilgai laikomame perteklyje šis santykis tik prastėja.',
}

/** What the phase does to the numbers, in the terms the numbers are in. */
const PHASE_HINT: Readonly<Record<GoalPhase, string>> = {
  cut: 'Daugiausia baltymų — 2,2 g kilogramui, kad kartu su riebalais nedingtų raumenys.',
  maintain: 'Tiek, kiek išeikvojate. Baltymai 1,8 g kilogramui.',
  bulk: 'Nedidelis perteklius: raumenys auga lėčiau, nei kaupiasi riebalai. Baltymai 1,6 g kilogramui.',
}

/** 7 700 kcal to a kilogram — the same constant the shared energy math uses. */
const KCAL_PER_KG = 7700

/**
 * How far back the trend check looks when the goal is older than that.
 *
 * Long enough for a real rate to show through water weight, short enough that a
 * target set in June is not judged on how February went. `weightTrendPerWeek`
 * needs a fortnight before it says anything at all.
 */
const TREND_WINDOW_DAYS = 90

const maxDate = (a: string | null, b: string): string => (a != null && a > b ? a : b)

/**
 * Hand the browser a file.
 *
 * A blob URL rather than a server route: the diary lives on the phone, so the
 * export works with no signal, and nothing has to be uploaded to be downloaded.
 * The UTF-8 marker is for Excel, which otherwise reads "Varškė" as mojibake.
 */
async function download(name: string, build: () => Promise<string>): Promise<void> {
  const csv = await build()
  const url = URL.createObjectURL(new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' }))
  const link = document.createElement('a')
  link.href = url
  link.download = `${name}-${localDateString(new Date(), deviceTimeZone())}.csv`
  link.click()
  URL.revokeObjectURL(url)
}

/** "−0,6 kg/sav." — signed, because the sign is the whole point. */
function formatRate(kgPerWeek: number): string {
  const rounded = Math.round(kgPerWeek * 10) / 10
  const sign = rounded > 0 ? '+' : rounded < 0 ? '−' : ''
  return `${sign}${Math.abs(rounded).toFixed(1).replace('.', ',')} kg/sav.`
}

/** "5 650" — five figures unspaced read as a code rather than as a count. */
const formatCount = (n: number): string => String(Math.round(n)).replace(/\B(?=(\d{3})+$)/g, ' ')

/**
 * Millions, because the only honest thing to say about a foreign database is
 * its order of magnitude: "~5,9 mln." is true all year, "5 912 447" is a lie
 * the moment somebody in France adds a yoghurt.
 */
function formatApprox(n: number): string {
  return `~${(n / 1_000_000).toFixed(1).replace(/\.0$/, '').replace('.', ',')} mln.`
}

function activityLabel(factor: number | null): string {
  if (factor == null) return '—'
  const match = ACTIVITY_OPTIONS.find((o) => ACTIVITY_FACTORS[o.value] === factor)
  // A factor from outside this list is still the factor the maths is using, so
  // it is shown as itself rather than as the nearest label it is not.
  return match?.label ?? `×${factor.toFixed(2).replace('.', ',')}`
}

export function Settings({ onSignedOut }: { onSignedOut: () => void }) {
  const [theme, setTheme] = useState<Theme>(storedTheme)
  const soundOn = useSoundSetting((state) => state.enabled)
  const [goalOpen, setGoalOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const [adminOpen, setAdminOpen] = useState(false)
  const [dossierOpen, setDossierOpen] = useState(false)
  /**
   * Numbers the app proposed, waiting in the goal sheet for a decision.
   *
   * A whole partial goal rather than a calorie figure, because ending a cut is
   * not only a calorie change: leaving 2,2 g/kg of protein in place under a
   * maintenance target is the cut's macros with the deficit removed.
   */
  const [suggestion, setSuggestion] = useState<Partial<GoalValues> | null>(null)
  const [pending, setPending] = useState(0)
  const today = localDateString(new Date(), deviceTimeZone())

  const { profile, save: saveProfile } = useProfile()
  const goal = useLiveQuery(() => goalForDate(today), [today])
  const weightKg = useLiveQuery(() => latestWeightKg(today), [today], null)
  /**
   * The weigh-ins the *current* target is answerable for.
   *
   * Regressing over the whole history compared this month's target against a
   * slope still dominated by last spring's cut: someone who had finished losing
   * and was holding steady got told they were losing too fast, with a card
   * offering them 300 kcal more a day. A target can only be judged by the
   * period it was actually in force, so the window starts where it did.
   */
  const trendFrom = maxDate(goal?.effectiveFrom ?? null, addDays(today, -TREND_WINDOW_DAYS))
  const weighIns = useLiveQuery(
    async () =>
      (await db.bodyMeasurements.orderBy('measuredOn').toArray())
        .filter(
          (row) => row.deletedAt == null && row.weightKg != null && row.measuredOn >= trendFrom,
        )
        .map((row) => ({ date: row.measuredOn, weightKg: row.weightKg as number })),
    [trendFrom],
    [],
  )

  const resting = restingEnergy(
    {
      weightKg,
      heightCm: profile?.heightCm ?? null,
      birthDate: profile?.birthDate ?? null,
      sex: profile?.sex ?? null,
      activityFactor: profile?.activityFactor ?? null,
    },
    today,
  )

  /**
   * The same resting burn from the other direction: lean mass rather than a
   * height-and-weight average. Only a scale reading supplies the lean mass,
   * so on a diary without one the row simply is not there. Informational —
   * the targets keep running on Mifflin, which every user has the inputs for.
   */
  const latestBody = useLiveQuery(() => latestMeasurements(today), [today], null)
  const bodyAge = profile?.birthDate ? ageYearsOn(profile.birthDate, today) : null
  const leanBurn =
    latestBody?.weightKg != null &&
    latestBody.bodyFatPct != null &&
    profile?.heightCm != null &&
    bodyAge != null &&
    profile.sex != null
      ? bodyDerived({
          weightKg: latestBody.weightKg,
          bodyFatPct: latestBody.bodyFatPct,
          muscleKg: latestBody.muscleKg ?? 0,
          heightCm: profile.heightCm,
          ageYears: bodyAge,
          sex: profile.sex,
        })
      : null
  const counts = useLiveQuery(
    async () => ({
      foods: await db.foods.count(),
      entries: await db.logEntries.count(),
      barcodes: await db.barcodes.count(),
    }),
    [],
  )
  /**
   * The catalogue as the server holds it. The phone's own count answers a
   * different question — how much is cached here — and showing only that made a
   * growing catalogue look like a shrinking one.
   */
  const catalogue = useQuery({
    queryKey: ['foods', 'stats'],
    queryFn: () =>
      api.get<{
        foods: number
        lithuanian: number
        barcodes: number
        lastScrape: string | null
        reachable: number
      }>('/api/foods/stats'),
    staleTime: 5 * 60_000,
  })

  /**
   * What the scale says against what the target implies.
   *
   * The intended rate is not stored anywhere and does not need to be: a target
   * below maintenance *is* an intention to lose, at exactly the rate the gap
   * implies. Comparing that with the measured trend is the whole check.
   *
   * It only ever suggests. Applying it opens the goal sheet with the number
   * filled in, because a tracker that quietly moves your target is a tracker
   * you cannot trust to have kept it.
   */
  const currentTarget = goal?.kcalTarget ?? DEFAULT_GOAL.kcalTarget
  const observedPerWeek = weightTrendPerWeek(weighIns)
  const intendedPerWeek =
    resting.tdee == null ? null : ((currentTarget - resting.tdee) * 7) / KCAL_PER_KG

  /**
   * Maintenance as this particular body actually demonstrated it.
   *
   * The formula above is a population average scaled by a guess from a list of
   * four; this is arithmetic on two things that were measured here — what was
   * eaten, and what the scale did about it. It is the number the suggestion
   * below corrects from, so someone who has been quietly eating 300 over target
   * gets advice about the diet they had rather than the one they meant to have.
   */
  const intake = useLiveQuery(() => intakeStats(trendFrom, today), [trendFrom, today], {
    meanKcal: null,
    loggedDays: 0,
  })
  const measuredTdee = observedTdee({
    meanIntakeKcal: intake.meanKcal,
    observedPerWeek,
    loggedDays: intake.loggedDays,
  })

  const trendCheck =
    intendedPerWeek == null
      ? null
      : checkTargetAgainstTrend({
          currentTarget,
          observedPerWeek,
          intendedPerWeek,
          observedTdee: measuredTdee,
        })

  /**
   * How long this has been going on for.
   *
   * A cut is a period, not a setting, and almost everything that goes wrong
   * with one is a function of its duration rather than of its depth — the drop
   * in expenditure, the training that gets worse, the evening it stops being
   * worth it. Nothing in any tracker tells you which week you are in, so the
   * decision to break or to stop gets made on mood. The goals are already
   * effective-dated, so the app can simply count.
   */
  const goals = useLiveQuery(() => goalHistory(), [], [])
  const maintenance = measuredTdee ?? resting.tdee
  // Without a maintenance figure every target reads as a hold, and a row saying
  // "Išlaikymas nuo sausio" over what is really a cut is worse than no row.
  const run = maintenance == null ? null : phaseRun(goals ?? [], maintenance, today)
  const advice = phaseAdvice(run)

  useEffect(() => {
    void getMeta<Theme>('theme', storedTheme()).then(setTheme)
    void countPending().then(setPending)
  }, [])

  const changeTheme = (next: Theme) => {
    setTheme(next)
    saveTheme(next)
  }

  const [toneError, setToneError] = useState<string | null>(null)
  const changeTone = (next: 'neutral' | 'sassy') => {
    setToneError(null)
    void saveProfile({ tone: next }).catch(() =>
      setToneError(sassLine('error_save', {}) ?? 'Nepavyko išsaugoti — reikia ryšio su serveriu.'),
    )
  }

  return (
    <div className="h-full overflow-y-auto overscroll-contain">
      <header className="border-b border-line px-4 py-2">
        <h1 className="font-display text-lg font-semibold text-ink">Nustatymai</h1>
      </header>

      <SectionLabel>Dienos tikslas</SectionLabel>
      <Card data-tour="settings-goal" className="mx-4">
        <ListRow
          title="Kalorijos"
          subtitle={
            // The phase is not stored: a target below maintenance is a cut, at
            // the rate the gap implies. Reading it back keeps one source of truth.
            resting.tdee == null
              ? undefined
              : `${PHASE_LABEL[phaseFromTarget(currentTarget, resting.tdee)]}${
                  // Only when the phase itself says something is happening. The
                  // rate had a lower bar than the phase did, so a target 80 kcal
                  // under maintenance read "Išlaikymas · −0,1 kg/sav." — a hold
                  // that claims to be losing.
                  intendedPerWeek != null &&
                  phaseFromTarget(currentTarget, resting.tdee) !== 'maintain'
                    ? ` · ${formatRate(intendedPerWeek)}`
                    : ''
                }`
          }
          value={`${goal?.kcalTarget ?? DEFAULT_GOAL.kcalTarget} kcal`}
          onActivate={() => setGoalOpen(true)}
        />
        <ListRow title="Baltymai" value={goal?.proteinG != null ? `${goal.proteinG} g` : '—'} />
        <ListRow title="Angliavandeniai" value={goal?.carbsG != null ? `${goal.carbsG} g` : '—'} />
        <ListRow title="Riebalai" value={goal?.fatG != null ? `${goal.fatG} g` : '—'} />
        <ListRow
          last={leanBurn == null && measuredTdee == null && run == null}
          title="Ramybės apykaita"
          subtitle={
            resting.tdee == null
              ? 'Užpildykite profilį, kad būtų apskaičiuota'
              : `Bazinė ${resting.bmr} kcal · su judrumu`
          }
          value={resting.tdee == null ? '—' : `${resting.tdee} kcal`}
        />
        {/* Only once the diary and the scale can answer it between them. A
            formula is an estimate about people in general; this one is about
            the person holding the phone. */}
        {leanBurn != null && (
          <ListRow
            last={measuredTdee == null && run == null}
            title="Apykaita pagal liesąją masę"
            subtitle={`Katch-McArdle · liesoji masė ${leanBurn.leanMassKg.toFixed(1).replace('.', ',')} kg`}
            value={`${leanBurn.bmrKcal} kcal`}
          />
        )}
        {measuredTdee != null && (
          <ListRow
            last={run == null}
            title="Išmatuota apykaita"
            subtitle={`Pagal ${intake.loggedDays} užfiksuotas dienas ir svorio kitimą`}
            value={`${measuredTdee} kcal`}
          />
        )}
        {run != null && (
          <ListRow
            last
            title="Etapas"
            subtitle={`${PHASE_LABEL[run.phase]} nuo ${run.startedOn}`}
            value={`${run.weeks}`}
            meta="sav."
          />
        )}
      </Card>

      {trendCheck != null &&
        trendCheck.suggestedTarget != null &&
        observedPerWeek != null &&
        intendedPerWeek != null && (
          <Card className="mx-4 mt-2 px-3 py-3">
            <p className="text-sm text-ink">
              {trendCheck.verdict === 'faster'
                ? 'Svoris keičiasi greičiau, nei numato tikslas.'
                : 'Svoris keičiasi lėčiau, nei numato tikslas.'}
            </p>
            <p className="tnum mt-1 text-sm text-mute">
              Pagal svarstykles {formatRate(observedPerWeek)} · pagal tikslą{' '}
              {formatRate(intendedPerWeek)}
            </p>
            <div className="mt-3 flex items-center gap-2">
              <Button
                onClick={() => {
                  setSuggestion({ kcalTarget: trendCheck.suggestedTarget ?? currentTarget })
                  setGoalOpen(true)
                }}
              >
                Siūlyti {trendCheck.suggestedTarget} kcal
              </Button>
              <span className="text-xs text-mute">Tikslas nekeičiamas be jūsų sutikimo</span>
            </div>
          </Card>
        )}

      {/* How long the phase has run, and what that length is worth saying about.
          Nothing else in the app knows a cut is a period rather than a setting,
          and the decision to break or to stop otherwise gets made on mood. */}
      {advice !== 'none' && run != null && (
        <Card className="mx-4 mt-2 px-3 py-3">
          <p className="text-sm text-ink">
            {PHASE_LABEL[run.phase]} trunka {run.weeks} sav.
          </p>
          <p className="mt-1 text-sm text-mute">{ADVICE_HINT[advice]}</p>
          {maintenance != null && (
            <div className="mt-3 flex items-center gap-2">
              <Button
                onClick={() => {
                  const plan =
                    weightKg == null
                      ? null
                      : planForPhase({ phase: 'maintain', tdee: maintenance, weightKg })
                  setSuggestion(
                    plan == null
                      ? { kcalTarget: maintenance }
                      : {
                          kcalTarget: plan.kcalTarget,
                          proteinG: plan.proteinG,
                          carbsG: plan.carbsG,
                          fatG: plan.fatG,
                        },
                  )
                  setGoalOpen(true)
                }}
              >
                Pereiti į išlaikymą
              </Button>
              <span className="text-xs text-mute">{maintenance} kcal</span>
            </div>
          )}
        </Card>
      )}

      {resting.tdee != null && (goal?.kcalTarget ?? 0) !== resting.tdee && (
        <div className="px-4 pt-2">
          <Button block onClick={() => setGoalOpen(true)}>
            Pasiūlyti tikslą pagal apykaitą
          </Button>
        </div>
      )}

      <SectionLabel>Profilis</SectionLabel>
      <Card className="mx-4">
        <ListRow
          title="Vardas"
          subtitle="Kuo jus vadinti"
          value={
            profile?.displayName == null || profile.displayName.trim() === ''
              ? '—'
              : profile.displayName
          }
          onActivate={() => setProfileOpen(true)}
        />
        <ListRow
          title="Ūgis"
          value={profile?.heightCm != null ? `${profile.heightCm} cm` : '—'}
          onActivate={() => setProfileOpen(true)}
        />
        <ListRow
          title="Gimimo data"
          value={profile?.birthDate ?? '—'}
          onActivate={() => setProfileOpen(true)}
        />
        <ListRow
          title="Lytis"
          value={profile?.sex == null ? '—' : profile.sex === 'male' ? 'Vyras' : 'Moteris'}
          onActivate={() => setProfileOpen(true)}
        />
        <ListRow
          title="Judrumas"
          value={activityLabel(profile?.activityFactor ?? null)}
          onActivate={() => setProfileOpen(true)}
        />
        {/* Read-only here on purpose: weight is a series of measurements, not a
            property, and it is recorded on the day it was measured. */}
        <ListRow
          last
          title="Svoris"
          subtitle="Įrašomas pagrindiniame ekrane"
          value={weightKg == null ? '—' : `${weightKg.toFixed(1).replace('.', ',')} kg`}
        />
      </Card>

      {profile?.isAdmin === true && (
        <>
          <SectionLabel>Katalogas</SectionLabel>
          <Card className="mx-4">
            <ListRow
              last
              title="Greito maisto redaktorius"
              subtitle="Sudedamosios ir patiekalai, kurių nesurenka scraperis"
              onActivate={() => setAdminOpen(true)}
            />
          </Card>
        </>
      )}

      <SectionLabel>Priminimai</SectionLabel>
      <RemindersCard />

      <SectionLabel>Išvaizda</SectionLabel>
      <div className="px-4">
        <Segmented
          label="Tema"
          value={theme}
          onValueChange={changeTheme}
          className="w-full"
          options={[
            { value: 'system', label: 'Sistemos' },
            { value: 'light', label: 'Šviesi' },
            { value: 'dark', label: 'Tamsi' },
          ]}
        />
      </div>

      {/* The voice, not the theme: 'Aštri' is the Wednesday register the app
          was built to speak in, 'Dalykiška' is the same app with its mouth
          shut. Lives on the profile, so it follows the person, not the phone. */}
      <SectionLabel>Nuotaika</SectionLabel>
      <div data-tour="settings-tone" className="px-4">
        <Segmented
          label="Nuotaika"
          value={profile?.tone ?? 'sassy'}
          onValueChange={changeTone}
          className="w-full"
          options={[
            { value: 'neutral', label: 'Dalykiška' },
            { value: 'sassy', label: 'Aštri' },
          ]}
        />
        <p className="pt-1.5 text-xs text-mute">
          Aštri — programėlė turi nuomonę apie jūsų dieną. Dalykiška — ta pati programėlė, tik
          tylesnė.
        </p>
        {toneError != null && <p className="pt-1 text-xs text-danger">{toneError}</p>}
      </div>

      {/* Deliberately not tied to the register above. „Dalykiška" turns her
          sentences down, not her presence off, and folding the two controls
          into one would turn a tone setting into a persona switch. */}
      <Card className="mx-4 mt-3">
        <ListRow
          title="Glitimės garsai"
          subtitle="Retai: pasiekimai, ceremonijos ir dienos pasisveikinimas"
          accessory={
            <Switch
              label="Glitimės garsai"
              checked={soundOn}
              onCheckedChange={(next) => {
                setSoundEnabled(next)
                // Play the thing being switched on, through the same path a
                // real cue takes — a toggle whose effect you cannot hear until
                // some unrelated moment next week is a toggle nobody trusts.
                if (next) playCue('notable')
              }}
            />
          }
        />
      </Card>

      <SectionLabel>Duomenys</SectionLabel>
      <Card className="mx-4">
        <ListRow
          title="Produktai kataloge"
          subtitle={
            catalogue.data == null
              ? 'Serveryje · reikia ryšio'
              : catalogue.data.lastScrape == null
                ? 'Serveryje'
                : `Serveryje · surinkta ${catalogue.data.lastScrape.slice(0, 10)}`
          }
          value={catalogue.data == null ? '—' : formatCount(catalogue.data.foods)}
        />
        {/* What the nightly crawls brought in, and only that: the number that
            stops growing the week a shop changes its pages. */}
        <ListRow
          title="Lietuvos produktai"
          subtitle="Iš parduotuvių: Rimi, Barbora, Lastmile"
          value={catalogue.data == null ? '—' : formatCount(catalogue.data.lithuanian)}
        />
        {/* Not stored anywhere — what the barcode ladder can still go and ask
            about, so an empty search does not read as an empty world. */}
        <ListRow
          title="Pasiekiama nuskenavus"
          subtitle="Open Food Facts ir USDA bazėse"
          value={catalogue.data == null ? '—' : formatApprox(catalogue.data.reachable)}
        />
        <ListRow
          title="Atsiųsta į telefoną"
          subtitle="Naudoti ir ieškoti be ryšio"
          value={formatCount(counts?.foods ?? 0)}
        />
        <ListRow title="Brūkšniniai kodai" value={formatCount(counts?.barcodes ?? 0)} />
        <ListRow title="Įrašai dienyne" value={formatCount(counts?.entries ?? 0)} />
        <ListRow
          title="Nesinchronizuota"
          value={formatCount(pending)}
          subtitle={pending > 0 ? 'Bus išsiųsta prisijungus' : 'Viskas išsiųsta'}
          onActivate={() => void syncNow().then(() => countPending().then(setPending))}
        />
        {/* Years of two people's meals should not depend on this app still
            running, on the VPS still being paid for, or on me still maintaining
            it. Every entry carries its own frozen macros, so one row per entry
            is the whole record rather than pointers into a catalogue. */}
        <ListRow
          title="Eksportuoti dienyną"
          subtitle="CSV — atsidaro skaičiuoklėje"
          onActivate={() => void download('gramas-dienynas', exportDiaryCsv)}
        />
        <ListRow
          last
          title="Eksportuoti svorį"
          subtitle="CSV — svoris, riebalai, juosmuo"
          onActivate={() => void download('gramas-svoris', exportMeasurementsCsv)}
        />
      </Card>
      <p className="px-4 pt-1.5 text-xs text-mute">
        Telefone laikoma tik tiek, kiek reikia paieškai be ryšio. Visas katalogas gyvena serveryje
        ir yra pasiekiamas paieškoje.
      </p>

      {/* Her dossier, not a changelog. Which build a phone is actually
          holding is still the first question worth asking when something
          behaves differently on his screen than on mine — that lives here
          too, filed as a case number, because a personnel file that omits
          its own build would be a strange kind of record-keeping. */}
      <SectionLabel>Byla</SectionLabel>
      <Card className="mx-4">
        {/* The four fixed lines became a file that fills itself in; the row
            below opens it. What stays here is the case number, because which
            build a phone is actually holding is still the first question worth
            asking when something behaves differently on his screen than mine. */}
        <ListRow
          title="Vardas: Glitimė"
          subtitle="Byla pildoma"
          onActivate={() => setDossierOpen(true)}
        />
        <ListRow title="Gramas" subtitle="Mitybos ir treniruočių dienynas" value={APP_VERSION} />
        {/* The way back in after „Daugiau neberodyti“. Without it that button
            is a one-way door, and the tour is where half of what this app does
            is explained at all. */}
        <ListRow
          last
          title="Apžvalga"
          subtitle="Pereiti ekskursiją dar kartą"
          onActivate={startTour}
        />
      </Card>
      <p className="px-4 pt-1.5 text-xs text-mute">{copyrightLine()}. Visos teisės saugomos.</p>

      <div className="px-4 py-6">
        <Button variant="danger" block onClick={() => void logout().then(onSignedOut)}>
          Atsijungti
        </Button>
      </div>

      <DossierSheet open={dossierOpen} onOpenChange={setDossierOpen} />

      <Sheet
        open={adminOpen}
        onOpenChange={setAdminOpen}
        size="full"
        title="Greito maisto redaktorius"
        description="Matoma tik administratoriui"
      >
        <Admin />
      </Sheet>

      <ProfileSheet
        open={profileOpen}
        onOpenChange={setProfileOpen}
        current={profile}
        onSave={saveProfile}
      />

      <GoalSheet
        open={goalOpen}
        onOpenChange={(open) => {
          setGoalOpen(open)
          if (!open) setSuggestion(null)
        }}
        suggested={resting.tdee}
        storedTarget={goal?.kcalTarget ?? DEFAULT_GOAL.kcalTarget}
        weightKg={weightKg}
        current={{
          kcalTarget: suggestion?.kcalTarget ?? goal?.kcalTarget ?? DEFAULT_GOAL.kcalTarget,
          proteinG: suggestion?.proteinG ?? goal?.proteinG ?? DEFAULT_GOAL.proteinG,
          carbsG: suggestion?.carbsG ?? goal?.carbsG ?? DEFAULT_GOAL.carbsG,
          fatG: suggestion?.fatG ?? goal?.fatG ?? DEFAULT_GOAL.fatG,
          fiberG: goal?.fiberG ?? null,
          sugarsG: goal?.sugarsG ?? null,
          satFatG: goal?.satFatG ?? null,
          saltG: goal?.saltG ?? null,
        }}
        today={today}
      />
    </div>
  )
}

interface GoalValues {
  kcalTarget: number
  proteinG: number | null
  carbsG: number | null
  fatG: number | null
  fiberG: number | null
  sugarsG: number | null
  satFatG: number | null
  saltG: number | null
}

interface ProfileValues {
  displayName: string
  heightCm: number | null
  birthDate: string
  sex: 'male' | 'female' | ''
  /**
   * Null when a factor is stored that is not one of the four offered — set
   * through the API, or under the older ladder these levels replaced. It is
   * left exactly as it is until the user picks a level themselves: falling back
   * to "Sėdimas" and saving it meant correcting a typo in a display name
   * silently rewrote maintenance by a thousand calories.
   */
  activity: ActivityLevel | null
}

function valuesFrom(current: SessionUser | null): ProfileValues {
  const stored = current?.activityFactor
  return {
    displayName: current?.displayName ?? '',
    heightCm: current?.heightCm ?? null,
    birthDate: current?.birthDate ?? '',
    sex: current?.sex ?? '',
    activity:
      ACTIVITY_OPTIONS.find((o) => ACTIVITY_FACTORS[o.value] === stored)?.value ??
      (stored == null ? 'sedentary' : null),
  }
}

function ProfileSheet({
  open,
  onOpenChange,
  current,
  onSave,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  current: SessionUser | null
  onSave: (patch: ProfilePatch) => Promise<void>
}) {
  const [values, setValues] = useState<ProfileValues>(() => valuesFrom(current))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * Read through a ref, so the reset below can depend on `open` alone.
   *
   * Depending on the record itself meant every background refresh — a sync
   * landing, a live query firing — reset the fields under whoever was typing in
   * them. Opening the sheet is the only moment "load what is stored" is right.
   */
  const latest = useRef(current)
  latest.current = current

  const [saved, setSaved] = useState<ProfileValues>(values)

  useEffect(() => {
    if (!open) return
    const fresh = valuesFrom(latest.current)
    setValues(fresh)
    setSaved(fresh)
    setError(null)
  }, [open])

  const dirty = JSON.stringify(values) !== JSON.stringify(saved)

  const save = async () => {
    if (saving) return
    setSaving(true)
    setError(null)
    try {
      await onSave({
        displayName: values.displayName.trim() === '' ? null : values.displayName.trim(),
        heightCm: values.heightCm,
        birthDate: values.birthDate.trim() === '' ? null : values.birthDate.trim(),
        sex: values.sex === '' ? null : values.sex,
        // Omitted, not nulled, when no level is selected: the route only writes
        // the field it is sent, so an unrecognised stored factor survives.
        ...(values.activity == null ? {} : { activityFactor: ACTIVITY_FACTORS[values.activity] }),
      })
      setSaved(values)
      onOpenChange(false)
    } catch {
      // The profile lives on the server, so this is the one settings change
      // that cannot be made offline; say so rather than failing silently.
      setError(sassLine('error_save', {}) ?? 'Nepavyko išsaugoti — reikia ryšio su serveriu.')
    } finally {
      setSaving(false)
    }
  }

  const set = <K extends keyof ProfileValues>(key: K, value: ProfileValues[K]) =>
    setValues((s) => ({ ...s, [key]: value }))

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      dirty={dirty && !saving}
      title="Profilis"
      description="Vardu jus pasveikina dienynas; ūgio, amžiaus ir lyties reikia ramybės apykaitai"
      footer={
        <Button
          variant="primary"
          block
          size="lg"
          disabled={saving || !dirty}
          onClick={() => void save()}
        >
          {saving ? 'Saugoma…' : dirty ? 'Išsaugoti' : 'Išsaugota'}
        </Button>
      }
    >
      <div className="flex flex-col gap-4 px-4 py-4">
        <Field
          label="Vardas"
          autoComplete="given-name"
          placeholder="Vardas"
          value={values.displayName}
          onChange={(e) => set('displayName', e.target.value)}
        />
        <NumberField
          label="Ūgis"
          suffix="cm"
          value={values.heightCm}
          onChange={(v) => set('heightCm', v)}
        />
        <Field
          label="Gimimo data"
          type="date"
          value={values.birthDate}
          onChange={(e) => set('birthDate', e.target.value)}
        />
        <Segmented
          label="Lytis"
          value={values.sex}
          onValueChange={(value) => set('sex', value as 'male' | 'female' | '')}
          className="w-full"
          options={[
            { value: 'male', label: 'Vyras' },
            { value: 'female', label: 'Moteris' },
          ]}
        />
        <Segmented
          label="Judrumas"
          value={values.activity ?? ''}
          onValueChange={(value) => set('activity', value as ActivityLevel)}
          className="w-full"
          options={ACTIVITY_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
        />
        <p className="text-xs text-mute">
          {values.activity == null
            ? 'Įrašytas ankstesnis judrumo lygis — pasirinkite naują, jei norite jį pakeisti.'
            : ACTIVITY_OPTIONS.find((o) => o.value === values.activity)?.hint}
        </p>
        <p className="text-xs text-mute">
          Treniruotės čia neįskaičiuojamos — jos pridedamos prie tos dienos limito atskirai.
        </p>
        <p className="text-xs text-mute">
          Svoris imamas iš paskutinio matavimo — jį įrašykite pagrindiniame ekrane.
        </p>
        {error && <p className="text-sm text-danger">{error}</p>}
      </div>
    </Sheet>
  )
}

function GoalSheet({
  open,
  onOpenChange,
  current,
  storedTarget,
  suggested,
  weightKg,
  today,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** What the fields open on — which is not the stored target when a suggestion put a number in them. */
  current: GoalValues
  /** The target as actually saved, so an accepted suggestion counts as a change. */
  storedTarget: number
  /** Maintenance calories from the profile, when it is complete enough. */
  suggested: number | null
  /** Latest weigh-in: the protein and fat floors are per kilogram of it. */
  weightKg: number | null
  today: string
}) {
  const [values, setValues] = useState<GoalValues>(current)
  const [saved, setSaved] = useState<GoalValues>(current)
  const [phase, setPhase] = useState<GoalPhase>('maintain')
  const [rate, setRate] = useState<number>(GOAL_PHASES.maintain.ratePerWeek)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * The values to load are read through a ref so that opening the sheet is the
   * only thing that loads them.
   *
   * `current` is built fresh on every render of the screen behind this sheet,
   * so a dependency on it re-ran this effect whenever anything on that screen
   * changed — a sync finishing, a weigh-in arriving — and quietly put the
   * stored target back over the one being typed. That was the bug behind "the
   * save buttons are extremely buggy": the number saved was not the number on
   * screen a moment earlier.
   */
  const latest = useRef({ current, suggested, storedTarget })
  latest.current = { current, suggested, storedTarget }

  useEffect(() => {
    if (!open) return
    const { current: fresh, suggested: tdee, storedTarget: stored } = latest.current
    setValues(fresh)
    // Measured against what is *stored*, not against what the fields opened on.
    // Arriving here by accepting a suggestion fills the target in already, and
    // comparing it with itself would leave Save greyed out on the one screen
    // whose whole purpose was to accept that number.
    setSaved({ ...fresh, kcalTarget: stored })
    setError(null)
    // Open on whatever the saved target already implies, so the switch shows
    // the phase you are in rather than asking you to declare it again.
    const inferred = phaseFromTarget(fresh.kcalTarget, tdee)
    setPhase(inferred)
    setRate(GOAL_PHASES[inferred].ratePerWeek)
  }, [open])

  const dirty = JSON.stringify(values) !== JSON.stringify(saved)

  /**
   * Apply a phase. It fills the fields below rather than replacing them,
   * because the fields are the answer — anyone following a coach's numbers can
   * ignore the switch entirely and type over whatever it put there.
   */
  const applyPhase = (next: GoalPhase, nextRate: number) => {
    setPhase(next)
    setRate(nextRate)
    if (suggested == null || weightKg == null) return
    const plan = planForPhase({ phase: next, tdee: suggested, weightKg, ratePerWeek: nextRate })
    setValues((s) => ({
      ...s,
      kcalTarget: plan.kcalTarget,
      proteinG: plan.proteinG,
      carbsG: plan.carbsG,
      fatG: plan.fatG,
    }))
  }

  // Shown as placeholders rather than prefilled values: a limit the user never
  // chose should stay null, so that changing the calorie target moves it too.
  const limits = defaultSecondaryTargets(values.kcalTarget)

  const save = async () => {
    if (saving) return
    setSaving(true)
    setError(null)
    try {
      // Effective from today rather than editing history: past days keep the
      // target they were actually judged against.
      await saveGoal({
        effectiveFrom: today,
        kcalTarget: values.kcalTarget,
        proteinG: values.proteinG,
        carbsG: values.carbsG,
        fatG: values.fatG,
        fiberG: values.fiberG,
        sugarsG: values.sugarsG,
        satFatG: values.satFatG,
        saltG: values.saltG,
      })
      setSaved(values)
      void syncNow()
      onOpenChange(false)
    } catch {
      setError(sassLine('error_save', {}) ?? 'Nepavyko išsaugoti. Pabandykite dar kartą.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      dirty={dirty && !saving}
      title="Dienos tikslas"
      description="Taikoma nuo šiandien — ankstesnės dienos nekeičiamos"
      footer={
        <div className="flex flex-col gap-2">
          {error && <p className="text-sm text-danger">{error}</p>}
          <Button
            variant="primary"
            block
            size="lg"
            disabled={saving || !dirty}
            onClick={() => void save()}
          >
            {saving ? 'Saugoma…' : dirty ? 'Išsaugoti' : 'Išsaugota'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3 px-4 pt-4">
        <Segmented
          label="Etapas"
          value={phase}
          onValueChange={(value) =>
            applyPhase(value as GoalPhase, GOAL_PHASES[value as GoalPhase].ratePerWeek)
          }
          className="w-full"
          options={PHASE_ORDER.map((p) => ({ value: p, label: PHASE_LABEL[p] }))}
        />

        {phase !== 'maintain' && (
          <div className="flex flex-wrap gap-2">
            {PHASE_RATES[phase].map((option) => (
              <Button
                key={option}
                size="sm"
                variant={option === rate ? 'primary' : undefined}
                onClick={() => applyPhase(phase, option)}
              >
                {phase === 'cut' ? '−' : '+'}
                {option.toFixed(option < 0.25 ? 3 : 2).replace('.', ',')} kg/sav.
              </Button>
            ))}
          </div>
        )}

        <p className="text-sm text-mute">
          {suggested == null
            ? 'Užpildykite profilį — be ūgio, amžiaus ir lyties etapas neturi iš ko skaičiuoti.'
            : weightKg == null
              ? 'Įrašykite svorį — baltymai ir riebalai skaičiuojami kilogramui kūno svorio.'
              : PHASE_HINT[phase]}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 px-4 py-4">
        <NumberField
          label="Kalorijos"
          suffix="kcal"
          value={values.kcalTarget}
          onChange={(v) => setValues((s) => ({ ...s, kcalTarget: v ?? 0 }))}
        />
        <NumberField
          label="Baltymai"
          suffix="g"
          value={values.proteinG}
          onChange={(v) => setValues((s) => ({ ...s, proteinG: v }))}
        />
        <NumberField
          label="Angliavandeniai"
          suffix="g"
          value={values.carbsG}
          onChange={(v) => setValues((s) => ({ ...s, carbsG: v }))}
        />
        <NumberField
          label="Riebalai"
          suffix="g"
          value={values.fatG}
          onChange={(v) => setValues((s) => ({ ...s, fatG: v }))}
        />
        <NumberField
          label="Skaidulos"
          suffix="g"
          hint="Ne mažiau"
          placeholder={String(limits.fiber)}
          value={values.fiberG}
          onChange={(v) => setValues((s) => ({ ...s, fiberG: v }))}
        />
        <NumberField
          label="Cukrus"
          suffix="g"
          hint="Ne daugiau"
          placeholder={String(limits.sugars)}
          value={values.sugarsG}
          onChange={(v) => setValues((s) => ({ ...s, sugarsG: v }))}
        />
        <NumberField
          label="Sočiosios r.r."
          suffix="g"
          hint="Ne daugiau"
          placeholder={String(limits.satFat)}
          value={values.satFatG}
          onChange={(v) => setValues((s) => ({ ...s, satFatG: v }))}
        />
        <NumberField
          label="Druska"
          suffix="g"
          hint="Ne daugiau"
          placeholder={String(limits.salt)}
          value={values.saltG}
          onChange={(v) => setValues((s) => ({ ...s, saltG: v }))}
        />
      </div>
      <p className="px-4 pb-4 text-xs text-mute">
        Palikus tuščią, taikoma ES rekomenduojama paros norma pagal jūsų kalorijų tikslą.
      </p>
    </Sheet>
  )
}
